import { Search, PencilRuler, Cog, LineChart } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useLocation } from 'react-router-dom';
import { isEnglishPath } from '../utils/i18nRoutes';

const stepsDe = [
  {
    icon: Search,
    title: 'Potenzialanalyse',
    description:
      'In einem kostenlosen Erstgespräch prüfen wir, welche Prozesse sich für KI-Automatisierung eignen und in welchen Kostenkategorien Einsparpotenzial besteht. Sie erhalten eine erste Einschätzung, ohne Verpflichtung.',
  },
  {
    icon: PencilRuler,
    title: 'Strategie und Planung',
    description:
      'Wir definieren gemeinsam die Prioritäten, erstellen einen Umsetzungsplan mit Meilensteinen und legen die Kennzahlen fest, an denen der Erfolg gemessen wird.',
  },
  {
    icon: Cog,
    title: 'Implementierung',
    description:
      'Wir setzen die KI-Lösungen technisch um, verhandeln Verträge für Kostenoptimierung neu und integrieren alles in Ihre bestehenden Systeme. Ihr Team wird in der Anwendung geschult.',
  },
  {
    icon: LineChart,
    title: 'Laufende Optimierung',
    description:
      'Nach der Implementierung überwachen wir die Ergebnisse, erstellen monatliche Reports und passen die Systeme an, wenn sich Ihre Prozesse oder Anforderungen ändern.',
  },
];

const stepsEn = [
  {
    icon: Search,
    title: 'Potential Analysis',
    description:
      'In a free initial consultation, we assess which processes are suitable for AI automation and in which cost categories savings potential exists. You receive an initial assessment with no obligation.',
  },
  {
    icon: PencilRuler,
    title: 'Strategy and Planning',
    description:
      'Together we define priorities, create an implementation plan with milestones, and establish the metrics by which success will be measured.',
  },
  {
    icon: Cog,
    title: 'Implementation',
    description:
      'We implement the AI solutions technically, renegotiate contracts for cost optimization, and integrate everything into your existing systems. Your team is trained in using the new tools.',
  },
  {
    icon: LineChart,
    title: 'Ongoing Optimization',
    description:
      'After implementation, we monitor the results, create monthly reports, and adapt the systems when your processes or requirements change.',
  },
];

function ProcessStep({ step, index, total }: { step: (typeof stepsDe)[0]; index: number; total: number }) {
  const { ref, isVisible } = useScrollAnimation();
  const delay = index * 0.1;

  return (
    <div ref={ref} className="relative">
      <div
        className={`card-base p-7 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
        style={{ animationDelay: `${delay}s` }}
      >
        <div className="w-12 h-12 bg-primary text-white rounded-xl flex items-center justify-center mb-5 font-bold text-lg">
          {index + 1}
        </div>
        <div className="w-12 h-12 bg-primary-tint rounded-xl flex items-center justify-center mb-5">
          <step.icon className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-lg font-bold text-dark dark:text-white mb-3">
          {step.title}
        </h3>
        <p className="text-muted dark:text-gray-300 leading-relaxed text-sm">
          {step.description}
        </p>
      </div>

      {index < total - 1 && (
        <div className="hidden lg:block absolute top-1/2 -right-4 z-10">
          <div className="w-8 h-px bg-primary-tint2"></div>
        </div>
      )}
    </div>
  );
}

export default function Process() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const location = useLocation();
  const isEn = isEnglishPath(location.pathname);
  const steps = isEn ? stepsEn : stepsDe;

  return (
    <section id="process" className="py-20 px-6 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className="text-center mb-12">
          <h2
            className={`text-2xl sm:text-3xl md:text-4xl font-bold text-dark dark:text-white mb-4 ${
              headerVisible ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            {isEn ? 'How a collaboration works' : 'Wie eine Zusammenarbeit abläuft'}
          </h2>
          <p
            className={`text-lg text-muted dark:text-gray-300 max-w-3xl mx-auto ${
              headerVisible ? 'animate-fade-in-up' : 'opacity-0'
            }`}
            style={{ animationDelay: '0.1s' }}
          >
            {isEn
              ? 'From the first conversation to ongoing optimization – four clearly defined steps whose progress you can follow at any time.'
              : 'Vom ersten Gespräch bis zur laufenden Optimierung – vier Schritte, die klar definiert sind und deren Fortschritt Sie jederzeit nachvollziehen können.'}
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-px bg-primary-tint2"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {steps.map((step, index) => (
              <ProcessStep key={step.title} step={step} index={index} total={steps.length} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
